import "./congratulationscreen.css";
import { useEffect, useCallback, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import { useTenant } from "../../context/TenantContext";
import { useClient } from "../../context/ClientContext";
import { useNPS } from "../../hooks/useNPS";
import FeedbackModal from "../../components/ui/FeedbackModal";
import InstagramShareButton from "./components/InstagramShareButton";
import TwitterShareButton from "./components/TwitterShareButton";
import Button from "../../components/ui/button";
import Loader from "../../components/ui/loader";
import { confetti } from "@tsparticles/confetti";

const CONFETTI_DEFAULTS = { startVelocity: 80, spread: 30, ticks: 160, zIndex: -9999 };

export default function CongratulationScreen() {
    const { t } = useLanguage();
    const { state } = useLocation();
    const navigate = useNavigate();
    const { tenanturlname } = useParams();
    const { tenant } = useTenant();
    const { client, isAuthenticated } = useClient();
    const { checkEligibility } = useNPS();
    const [showNPS, setShowNPS] = useState(false);
    const [npsChecked, setNpsChecked] = useState(false);
    
    const {
        reward,
        chances,
        campaignId,
        congratsImage,
        tnxId,
        imageUrl,
        campaignType,
        campaignConfig,
        winAnimations,
    } = state || {};

    // Find matching animation based on reward value
    const getWinAnimation = () => {
        if (!winAnimations || !Array.isArray(winAnimations) || winAnimations.length === 0) {
            return null;
        }
        
        // Try to find exact match first
        const rewardNum = parseInt(reward);
        const rewardStr = String(reward);
        
        let matchingAnimation = winAnimations.find(
            (anim) => {
                const animReward = anim.reward;
                // Try multiple comparison methods
                return animReward === reward || 
                       animReward === rewardNum || 
                       animReward === rewardStr ||
                       parseInt(animReward) === rewardNum ||
                       String(animReward) === rewardStr;
            }
        );
        
        // If no exact match found but winAnimations exists, use the first animation
        if (!matchingAnimation && winAnimations.length > 0) {
            matchingAnimation = winAnimations[0];
        }
        
        // Always return matching animation, never fallback to congratsImage
        return matchingAnimation?.animation || null;
    };

    const displayImage = getWinAnimation();

    const confettiFire = useCallback(() => {
        const particleCount = 100;
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        confetti(
            Object.assign({}, CONFETTI_DEFAULTS, {
                particleCount,
                origin: { x: 0, y: isMobile ? 0.3 : 0.5 },
                colors: ["#FFC100", "#002ED0"],
                angle: 15,
            })
        );
        confetti(
            Object.assign({}, CONFETTI_DEFAULTS, {
                particleCount,
                origin: { x: 1, y: isMobile ? 0.3 : 0.5 },
                colors: ["#FFC100", "#002ED0"],
                angle: 150,
            })
        );
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            confettiFire();
        }, 2000);
        return () => clearTimeout(timer);
    }, [confettiFire]);

    // Check NPS eligibility 5 seconds after landing on congratulations page
    useEffect(() => {
        const npsConfig = tenant?.tenantmasterconfig?.nps_config;
        if (!npsConfig?.enabled || !npsConfig?.triggers?.spin_wheel?.enabled) return;
        if (!isAuthenticated || !client?.clientId || npsChecked) return;

        const timer = setTimeout(async () => {
            setNpsChecked(true);
            try {
                const result = await checkEligibility('spin_wheel');
                if (result?.eligible) {
                    setShowNPS(true);
                }
            } catch (error) {
                // NPS check failed silently
            }
        }, 5000);

        return () => clearTimeout(timer);
    }, [isAuthenticated, client?.clientId, npsChecked, checkEligibility, tenant]);

    const onSpin = () => {
        // Route requires :campaignId
        if (!campaignId) return;
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'cta_click', ctaName: 'play_again', campaignId: String(campaignId) });
        navigate(`/${tenanturlname}/gamification/spinwheel/${campaignId}`);
    };

    if (!state) {
        return <Loader fullscreen label={t("homePage.loading")} size="xl" />;
    }

    return (
        <div style={{ minHeight: "100vh", zIndex: 99 }}>
            <div className="align-center">
                <div style={{ marginTop: "50px" }}>
                    <div style={{ fontWeight: "bolder", textAlign: "center" }}>
                        <h5 style={{ fontSize: "24px", margin: "0 auto" }}>
                            {t("gamification.congratulations") || "Congratulations!"}
                        </h5>
                        <h2 style={{ fontWeight: "bolder", color: "blue", textAlign: "center" }}>
                            <span style={{ fontSize: "100px", color: "#1434CB" }}>
                                {reward}
                            </span>
                            <span style={{ fontSize: "46px", color: "#1434CB" }}>
                                {t("gamification.yen") || "円"}
                            </span>
                        </h2>
                        <h5
                            style={{
                                textAlign: "center",
                                fontSize: "24px",
                                margin: "0 auto",
                            }}
                        >
                            {t("gamification.youHaveWon") || "You have won!"}
                        </h5>
                    </div>
                </div>
            </div>
            <div className="align-center">
                <div>
                    {displayImage && (
                        <img src={displayImage} alt="Congratulations" className="vee-congrats-image" />
                    )}
                </div>
            </div>
            <div style={{ backgroundColor: "white" }}>
                <br />
                <div className="align-center">
                    <div style={{ textAlign: "center", width: "100%" }}>
                        <span
                            style={{
                                color: "#1434CB",
                                fontSize: "18px",
                                display: "block",
                                marginBottom: "10px",
                            }}
                        >
                            {t("gamification.shareOnSocialMedia") || "Share on Social Media"}
                        </span>
                        <div style={{ display: "flex", justifyContent: "center", gap: "10px" }}>
                            <InstagramShareButton
                                campaignType={campaignType}
                                imageUrl={imageUrl}
                                reward={reward}
                                chances={chances}
                                campaignId={campaignId}
                                congratsImage={congratsImage}
                                tnxId={tnxId}
                                winAnimations={winAnimations}
                                campaignConfig={campaignConfig}
                            />
                            <TwitterShareButton
                                campaignType={campaignType}
                                imageUrl={imageUrl}
                                reward={reward}
                                chances={chances}
                                campaignId={campaignId}
                                congratsImage={congratsImage}
                                tnxId={tnxId}
                                winAnimations={winAnimations}
                                campaignConfig={campaignConfig}
                            />
                        </div>
                    </div>
                </div>
                <br />
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", width: "100%", margin: "20px 0" }}>
                    <div style={{ fontSize: "14px", textAlign: "center", maxWidth: "600px", width: "100%", padding: "0 20px", wordWrap: "break-word", overflowWrap: "break-word" }}>
                        {t("gamification.winningDesc") || "Your reward has been credited to your account."}
                    </div>
                </div>
                <br />
                {campaignType === "SPIN" && (
                    <>
                        <div style={{ fontSize: "16px", textAlign: "center", whiteSpace: "nowrap", width: "100%" }}>
                            <span>{t("gamification.youHave") || "You have"}</span>
                            <span> {chances - 1} </span>
                            <span>{t("gamification.remainingSpin") || "remaining spins"}</span>
                        </div>
                        <br />
                        <div className="align-center" style={{ paddingLeft: "20px", paddingRight: "20px", width: "100%" }}>
                            {chances <= 1 ? (
                                <Button
                                    variant="secondary"
                                    disabled
                                    style={{
                                        paddingLeft: "10px",
                                        backgroundColor: "#cccccc",
                                        color: "#666666",
                                        width: "100%",
                                    }}
                                >
                                    {t("gamification.playAgain") || "Play Again"}
                                </Button>
                            ) : (
                                <Button
                                    variant="primary"
                                    onClick={onSpin}
                                    style={{
                                        paddingLeft: "10px",
                                        backgroundColor: "#1434CB",
                                        width: "100%",
                                    }}
                                >
                                    {t("gamification.playAgain") || "Play Again"}
                                </Button>
                            )}
                        </div>
                    </>
                )}
                <br />
            </div>

            {/* NPS Feedback Modal — triggers 5s after landing on this page */}
            {showNPS && (
                <FeedbackModal
                    open={showNPS}
                    onClose={() => setShowNPS(false)}
                    triggerType="spin_wheel"
                    channel="IN_APP"
                    npsData={{ rewardAmount: reward, campaignId, campaignType, remainingChances: chances }}
                />
            )}
        </div>
    );
}
